/**
 * SENTRY INITIALIZATION - FRONTEND
 * =================================
 * Configuração do Sentry para error tracking e performance monitoring
 * no frontend React + Vite
 *
 * Baseado em: https://docs.sentry.io/platforms/javascript/guides/react/
 * Best practices: https://docs.sentry.io/platforms/javascript/configuration/filtering/
 */

import * as Sentry from '@sentry/react';

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE;

  if (!dsn) {
    console.warn('[Sentry] DSN não configurado - Sentry desabilitado');
    return;
  }

  Sentry.init({
    dsn,
    environment,

    // Versão da release (para tracking de deploys)
    release: import.meta.env.VITE_RELEASE_VERSION || `web@${environment}-${Date.now()}`,

    // Integrations
    integrations: [
      // React Router integration (Wouter compatible)
      Sentry.browserTracingIntegration(),

      // Replay para debugging visual
      Sentry.replayIntegration({
        maskAllText: false, // Não mascarar texto (ajuste conforme LGPD)
        blockAllMedia: false, // Não bloquear mídia
      }),

      // Browser profiling (performance)
      Sentry.browserProfilingIntegration(),

      // Third-party error filtering (erros de extensões, widgets)
      // Requer @sentry/vite-plugin com applicationKey
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ['chatbot-imoveis-web'],
        behaviour: 'drop-error-if-contains-third-party-frames',
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0, // 10% em prod, 100% em dev

    // Session Replay
    replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0, // 10% de sessões
    replaysOnErrorSampleRate: 1.0, // 100% quando há erro

    // Before Send - Filtrar dados sensíveis
    beforeSend(event, hint) {
      // Remover informações sensíveis
      if (event.request) {
        // Remover headers sensíveis
        if (event.request.headers) {
          delete event.request.headers['Authorization'];
          delete event.request.headers['Cookie'];
          delete event.request.headers['X-Firebase-Token'];
        }

        // Remover query params sensíveis
        if (event.request.url) {
          try {
            const url = new URL(event.request.url);
            url.searchParams.delete('token');
            url.searchParams.delete('api_key');
            url.searchParams.delete('password');
            url.searchParams.delete('apiKey');
            url.searchParams.delete('apikey');
            event.request.url = url.toString();
          } catch (e) {
            // URL inválida, ignorar
          }
        }
      }

      // Log em desenvolvimento (mas ainda envia para testar)
      if (environment === 'development') {
        console.log('[Sentry] Sending event in development:', event);
      }

      return event;
    },

    // Ignore erros conhecidos/esperados (baseado em docs oficiais)
    ignoreErrors: [
      // Network errors (comuns e não acionáveis)
      /^Network request failed$/i,
      /^NetworkError$/i,
      /^Failed to fetch$/i,
      /^Load failed$/i,
      /^Timeout$/i,
      /^cancelled$/i,

      // Erros de abort (esperados quando usuário cancela)
      /^AbortError$/i,
      /^The operation was aborted$/i,
      /^The user aborted a request$/i,

      // Erros do Safari
      /^NotSupportedError$/i,
      /^UnknownError$/i,
      /^SecurityError$/i,
      /^NotAllowedError$/i,

      // ResizeObserver (bug conhecido do Chrome)
      /^ResizeObserver loop/i,
      /^ResizeObserver loop completed with undelivered notifications/i,

      // Script errors genéricos (third-party)
      /^Script error\.?$/i,
      /^Javascript error: Script error\.?$/i,

      // Erros de extensões do navegador
      'top.GLOBALS',
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      /^safari-web-extension:\/\//i,
      /^edge-extension:\/\//i,

      // Ad blockers
      /AdBlock/i,
      /Adblock/i,
      /uBlock/i,
      /adblock/i,

      // Facebook SDK
      /graph\.facebook\.com/i,
      /connect\.facebook\.net/i,
      /fb_xd_fragment/i,

      // Google Analytics
      /google-analytics\.com/i,
      /googletagmanager\.com/i,
      /analytics\.js/i,
      /gtag/i,

      // Outros erros comuns de third-party
      /Cannot read property.*of undefined/i, // Muito genérico, pode ser third-party
      /null is not an object/i, // Muito genérico
      /undefined is not a function/i, // Muito genérico
      /Can't find variable/i, // Safari third-party
    ],

    // Deny URLs - não reportar erros de extensões/third-party
    denyUrls: [
      // Extensões de navegador
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      /^safari-web-extension:\/\//i,
      /^edge-extension:\/\//i,
      /^opera:\/\//i,

      // Facebook
      /graph\.facebook\.com/i,
      /connect\.facebook\.net/i,
      /staticxx\.facebook\.com/i,
      /static\.xx\.fbcdn\.net/i,

      // Google Analytics & Tag Manager
      /google-analytics\.com\/analytics\.js/i,
      /googletagmanager\.com/i,
      /www\.google-analytics\.com/i,

      // Outros CDNs third-party conhecidos
      /cdn\.jsdelivr\.net/i,
      /unpkg\.com/i,
      /cdnjs\.cloudflare\.com/i,

      // Scripts de anúncios
      /pagead2\.googlesyndication\.com/i,
      /adservice\.google\.com/i,
      /googleads\.g\.doubleclick\.net/i,
    ],

    // Allow URLs - apenas nossos domínios (opcional, mas recomendado)
    allowUrls: [
      /chatbotimoveis\.com\.br/i,
      /chatbot-imoveis-web\.pages\.dev/i,
      /localhost/i,
      /127\.0\.0\.1/i,
    ],
  });

  console.log(
    `[Sentry] Inicializado com sucesso (environment: ${environment}, release: ${import.meta.env.VITE_RELEASE_VERSION || 'unknown'})`
  );
}

/**
 * Capturar exceção manualmente
 */
export function captureException(error: Error, context?: Record<string, any>) {
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capturar mensagem manualmente
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level);
}

/**
 * Setar contexto de usuário
 */
export function setUser(user: { id: string; email?: string; name?: string } | null) {
  Sentry.setUser(user);
}

/**
 * Adicionar breadcrumb (rastro de eventos)
 */
export function addBreadcrumb(message: string, category: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}

/**
 * Criar span para performance tracking
 */
export function startSpan<T>(
  options: {
    op: string;
    name: string;
    attributes?: Record<string, any>;
  },
  callback: () => T
): T {
  return Sentry.startSpan(options, callback);
}
